import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, ButtonGroup, Spinner } from "react-bootstrap";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf.mjs";
import { downloadDocument } from "../../requests/agreementdocuments";
import { base64ToArrayBuffer, downloadFileFromBase64Data, getFileExtension } from "../../utils/documentsHelper";

GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();

// Caps canvas size so long documents stay within iOS Safari canvas memory limits.
const MAX_PIXEL_RATIO = 2;
const MAX_PIXELS_PER_PAGE = 4000000;
const ZOOM_STEP = 0.25;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const CONTAINER_PADDING = 16;

const toolbarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "8px",
  marginBottom: "8px",
};

const messageStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  minHeight: "300px",
  backgroundColor: "#F5F5F5",
  borderRadius: "4px",
  color: "#555",
};

const getContainerWidth = (container) => Math.max(container.clientWidth - CONTAINER_PADDING * 2, 100);

// Loads the document bytes through the authenticated API (the S3 bucket is private)
// and renders every page to a canvas, which works on iOS Safari where an
// iframe/embed only shows the first page of a PDF.
const PdfViewer = ({ candidateId, documentId, fileUrl, fileName, height, onLoad }) => {
  const containerRef = useRef(null);
  const pagesRef = useRef(null);
  const [base64Content, setBase64Content] = useState(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [status, setStatus] = useState("loading");
  const [fitScale, setFitScale] = useState(null);
  const [scale, setScale] = useState(null);
  // While true the document follows the container width, e.g. when the phone is rotated.
  const isFitWidthRef = useRef(true);
  const extension = getFileExtension(fileUrl);
  const isPdf = extension === "pdf";

  // Fetches the document and works out the initial zoom.
  useEffect(() => {
    let isCancelled = false;
    let loadedDocument = null;

    const load = async () => {
      try {
        const response = await downloadDocument(candidateId, documentId);
        const content = response?.data?.pdfContent;
        if (!content || response.data.isError) throw new Error("Document content not available");
        if (isCancelled) return;

        setBase64Content(content);
        if (isPdf) {
          loadedDocument = await getDocument({ data: base64ToArrayBuffer(content), isEvalSupported: false }).promise;
          if (isCancelled) return;

          const firstPage = await loadedDocument.getPage(1);
          const pageWidth = firstPage.getViewport({ scale: 1 }).width;
          const initialFitScale = getContainerWidth(containerRef.current) / pageWidth;
          setFitScale(initialFitScale);
          setScale(initialFitScale);
          setPdfDocument(loadedDocument);
        }
        if (!isCancelled) setStatus("loaded");
      } catch (error) {
        if (!isCancelled) setStatus("error");
      } finally {
        if (!isCancelled) onLoad(false);
      }
    };

    load();

    return () => {
      isCancelled = true;
      if (loadedDocument) loadedDocument.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, documentId]);

  // Keeps "fit width" correct when the phone rotates or the window resizes.
  useEffect(() => {
    if (!pdfDocument) return undefined;
    let timeoutId;

    const onResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        if (!containerRef.current) return;
        const firstPage = await pdfDocument.getPage(1);
        const newFitScale = getContainerWidth(containerRef.current) / firstPage.getViewport({ scale: 1 }).width;
        setFitScale(newFitScale);
        if (isFitWidthRef.current) setScale(newFitScale);
      }, 250);
    };

    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", onResize);
    };
  }, [pdfDocument]);

  // Renders every page at the current zoom.
  useEffect(() => {
    const pagesContainer = pagesRef.current;
    if (!pdfDocument || !scale || !pagesContainer) return undefined;

    let isCancelled = false;
    const renderTasks = [];

    const renderPages = async () => {
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
        const page = await pdfDocument.getPage(pageNumber);
        if (isCancelled) return;

        const viewport = page.getViewport({ scale });
        const pixelBudgetRatio = Math.sqrt(MAX_PIXELS_PER_PAGE / (viewport.width * viewport.height));
        const outputScale = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO, pixelBudgetRatio);

        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        // !important because common.css has global signature-pad rules (canvas { width: 100% } and,
        // on phones, canvas { height: 90px !important }) that would otherwise squash every page.
        const pageStyles = {
          width: `${Math.floor(viewport.width)}px`,
          height: `${Math.floor(viewport.height)}px`,
          "max-width": "none",
          display: "block",
          margin: "0 auto 12px",
          padding: "0",
          border: "none",
          "background-color": "#FFF",
          "box-shadow": "0 1px 4px rgba(0, 0, 0, 0.2)",
        };
        Object.entries(pageStyles).forEach(([property, value]) => canvas.style.setProperty(property, value, "important"));
        pagesContainer.appendChild(canvas);

        const renderTask = page.render({
          canvasContext: canvas.getContext("2d"),
          viewport,
          transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
        });
        renderTasks.push(renderTask);
        try {
          await renderTask.promise;
        } catch (error) {
          return; // cancelled by a zoom change or unmount
        }
      }
    };

    renderPages();

    return () => {
      isCancelled = true;
      renderTasks.forEach((task) => task.cancel());
      // Shrinking canvases releases their memory straight away on iOS Safari.
      pagesContainer.querySelectorAll("canvas").forEach((canvas) => {
        canvas.width = 0;
        canvas.height = 0;
      });
      pagesContainer.innerHTML = "";
    };
  }, [pdfDocument, scale]);

  const zoomOut = () => {
    isFitWidthRef.current = false;
    setScale((current) => Math.max(current - ZOOM_STEP, Math.min(MIN_SCALE, fitScale)));
  };
  const zoomIn = () => {
    isFitWidthRef.current = false;
    setScale((current) => Math.min(current + ZOOM_STEP, MAX_SCALE));
  };
  const fitToWidth = () => {
    isFitWidthRef.current = true;
    setScale(fitScale);
  };
  const download = () => downloadFileFromBase64Data(base64Content, fileName, extension);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {status === "loading" && (
        <div style={messageStyle}>
          <Spinner animation="border" role="status" />
          <span>Loading document...</span>
        </div>
      )}
      {status === "error" && (
        <div style={messageStyle}>
          <span>Unable to load this document. Please try again later.</span>
        </div>
      )}
      {status === "loaded" && !isPdf && (
        <div style={messageStyle}>
          <span>This document can't be previewed. Download it to view.</span>
          <Button variant="outline-secondary" size="sm" onClick={download}>
            <span className="qjicon-down" style={{ marginRight: "6px" }} />
            Download
          </Button>
        </div>
      )}
      {status === "loaded" && isPdf && (
        <div style={toolbarStyle}>
          <ButtonGroup size="sm">
            <Button variant="outline-secondary" onClick={zoomOut} aria-label="Zoom out">
              -
            </Button>
            <Button variant="outline-secondary" disabled style={{ minWidth: "64px" }}>
              {`${Math.round(scale * 100)}%`}
            </Button>
            <Button variant="outline-secondary" onClick={zoomIn} aria-label="Zoom in">
              +
            </Button>
            <Button variant="outline-secondary" onClick={fitToWidth}>
              Fit width
            </Button>
          </ButtonGroup>
          <Button variant="outline-secondary" size="sm" onClick={download}>
            <span className="qjicon-down" style={{ marginRight: "6px" }} />
            Download
          </Button>
        </div>
      )}
      <div
        ref={pagesRef}
        className="customScrollbar"
        style={{
          display: status === "loaded" && isPdf ? "block" : "none",
          width: "100%",
          maxHeight: height,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          backgroundColor: "#F5F5F5",
          borderRadius: "4px",
          padding: `${CONTAINER_PADDING}px`,
        }}
      />
    </div>
  );
};

PdfViewer.propTypes = {
  candidateId: PropTypes.string.isRequired,
  documentId: PropTypes.string.isRequired,
  fileUrl: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  height: PropTypes.string,
  onLoad: PropTypes.func,
};

PdfViewer.defaultProps = {
  fileUrl: "",
  height: "75vh",
  onLoad: () => {},
};

export default PdfViewer;
